/* Scoped stock checks for TSN private quantity components. No inventory counts in responses. */
(() => {
  if (window.TSNStockPrivacy) return;
  const checkPath = document.currentScript?.dataset.checkUrl || '/search';
  const unavailable = "Sorry, we don't have that many available.";
  const retry = "We couldn't check availability. Please try again.";
  const busy = new WeakSet();
  const replay = new WeakSet();

  async function check(handle, variant, quantity, mode = 'add', lineKey = '') {
    if (!Number.isSafeInteger(Number(quantity)) || Number(quantity) < 0) return false;
    const url = new URL(checkPath, location.origin);
    url.searchParams.set('view', 'tsn-stock-check');
    url.searchParams.set('q', [handle, variant, quantity, mode, lineKey].join('|'));
    url.searchParams.set('_tsn', `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    if (window.Shopify?.theme?.role !== 'main' && window.Shopify?.theme?.id) {
      url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Availability check failed');
      const data = await response.json();
      if (data.version !== 1 || data.valid !== true || typeof data.allowed !== 'boolean') throw new Error('Invalid availability response');
      return data.allowed;
    } finally { clearTimeout(timer); }
  }

  function quantityComponent(form) {
    return Array.from(document.querySelectorAll('[data-tsn-private-quantity]'))
      .find(root => root.querySelector('input[name="quantity"]')?.form === form)?.tsnStockQuantity;
  }

  function showError(form, component, message) {
    if (component) { component.showStockError(message); return; }
    let status = form.querySelector('[data-tsn-stock-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.tsnStockStatus = '';
      status.setAttribute('role', 'status');
      form.append(status);
    }
    status.textContent = message;
  }

  async function guard(event) {
    const button = event.type === 'click' ? event.target.closest('button[name="add"]') : event.submitter;
    const form = event.type === 'submit' ? event.target : button?.form;
    if (!form || !button || replay.has(button)) return;
    const component = quantityComponent(form);
    if (!form.dataset.tsnStockHandle) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy.has(form)) return;
    busy.add(form);
    button.setAttribute('aria-busy', 'true');
    const quantity = Number(form.elements.namedItem('quantity')?.value || 1);
    const variant = form.elements.namedItem('id')?.value;
    form.querySelector('[data-tsn-stock-status]')?.remove();
    try {
      const items = new Map([[String(variant), {handle: form.dataset.tsnStockHandle, variant, quantity}]]);
      const addonRoot = form.querySelector('[data-tsn-product-add-ons]');
      const addonCheckbox = addonRoot?.querySelector('[data-tsn-addon-checkbox]');
      const addonSelected = Boolean(addonCheckbox?.checked);
      if (addonSelected) {
        addonRoot.querySelectorAll('[data-tsn-addon-item]').forEach(item => {
          const id = item.dataset.variantId;
          const count = Math.max(1, Number(item.dataset.baseQuantity) || 1) * quantity;
          const existing = items.get(id);
          if (existing) existing.quantity += count;
          else items.set(id, {handle: item.dataset.productHandle, variant: id, quantity: count});
        });
      }
      const results = await Promise.all(Array.from(items.values()).map(item => check(item.handle, item.variant, item.quantity)));
      const allowed = results.every(Boolean);
      if (addonSelected !== Boolean(addonCheckbox?.checked)) return;
      if (!form.isConnected || variant !== form.elements.namedItem('id')?.value || quantity !== Number(form.elements.namedItem('quantity')?.value || 1)) return;
      if (!allowed) { showError(form, component, unavailable); return; }
      if (component) component.errInventory = false;
      replay.add(button);
      try { button.click(); } finally { replay.delete(button); }
    } catch (_) {
      if (form.isConnected) showError(form, component, retry);
    } finally {
      busy.delete(form);
      button.removeAttribute('aria-busy');
    }
  }

  function watchError(element) {
    if (element.dataset.tsnErrorObserved) return;
    element.dataset.tsnErrorObserved = 'true';
    const redact = () => {
      const text = element.textContent || '';
      if (/\b(?:only|all)\s+[\d,]+\s+(?:items?|units?|left)|\b[\d,]+\s+(?:left in stock|available in stock)/i.test(text)) {
        element.textContent = unavailable;
      }
    };
    new MutationObserver(redact).observe(element, {childList: true, subtree: true, characterData: true});
    redact();
  }

  async function guardCart(input, handle, variant, lineKey, quantity, apply) {
    if (!input || input.dataset.tsnPending === 'true') return;
    const prior = Number(input.dataset.tsnOriginalQty || input.defaultValue || 0);
    const observed = input.value;
    input.dataset.tsnPending = 'true';
    try {
      const allowed = await check(handle, variant, quantity, 'replace', lineKey);
      if (!input.isConnected || input.value !== observed) return;
      if (allowed) {
        input.dataset.tsnOriginalQty = String(quantity);
        apply();
      } else {
        input.value = String(prior);
        const error = input.closest('.cart-item')?.querySelector('.cart-item__error');
        if (error) {
          error.querySelector('.cart-item__error-text').textContent = unavailable;
          error.classList.remove('hidden');
        }
      }
    } catch (_) {
      if (input.isConnected) input.value = String(prior);
    } finally {
      input.dataset.tsnPending = 'false';
    }
  }

  function guardCartInput(input) {
    if (!input?.max || Number(input.value) <= Number(input.max)) return;
    input.value = input.dataset.tsnOriginalQty || input.defaultValue || '1';
    const error = input.closest('.cart-item')?.querySelector('.cart-item__error');
    const message = error?.querySelector('.cart-item__error-text');
    if (message) {
      message.textContent = unavailable;
      error.classList.remove('hidden');
    }
  }

  window.TSNStockPrivacy = { check, unavailable, retry, watchError, guardCart, guardCartInput };
  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);
})();
